import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Safari ITP fallback: also persist the access/refresh tokens in
      // localStorage so the axios interceptor can attach them as Bearer
      // even when the HTTP-only cookie gets dropped cross-site.
      tokenStore.set(data);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, referralCode, promoCode, testerTag) => {
    try {
      const body = { email, password, name };
      if (referralCode && referralCode.trim()) {
        body.referral_code = referralCode.trim().toUpperCase();
      }
      if (promoCode && promoCode.trim()) {
        body.promo_code = promoCode.trim().toUpperCase();
      }
      if (testerTag && testerTag.trim()) {
        body.tester_tag = testerTag.trim().slice(0, 48);
      }
      const { data } = await api.post("/auth/register", body);
      tokenStore.set(data);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    tokenStore.clear();
    setUser(false);
  };

  const isPremium = !!(user && user.is_premium);
  const subscriptionStatus = user ? user.subscription_status : null;
  const trialDaysRemaining = user ? user.trial_days_remaining : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        refresh: fetchMe,
        isPremium,
        subscriptionStatus,
        trialDaysRemaining,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
